<template>
  <b-card>
    <layout-loading v-if="itemLoading" />
    <b-form>
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="page-title"
          >
            <b-form-input
              id="page-title"
              v-model="form.title"
              :state="validateState('title')"
              @change="updateAltImage()"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.slug')"
            label-for="page-slug"
          >
            <b-form-input
              id="page-slug"
              v-model="form.slug"
              :state="validateState('slug')"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.priority')"
            label-for="page-sort"
          >
            <b-form-input
              id="page-sort"
              v-model.number="form.sort"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="page-lang"
          >
            <languageSelector
              v-model="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.category')"
            label-for="page-category"
            :state="validateState('category')"
          >
            <v-select-custom
              id="page-category"
              v-model="form.category"
              :options="pageCategories"
            />
            <b-form-invalid
              id="input-category-feedback"
              :label="$t('general.category')"
              :validator="$v.form.category"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.editor')"
            label-for="page-editor"
          >
            <v-select-custom
              id="page-editor"
              v-model="form.editor"
              :custom-props="{ label: 'fullName' }"
              :options="editorOptions"
              @search="fetchUsersByRole({role: 'editor', 'search' : $event}, form.lang)"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.tags')"
            label-for="page-tag"
            :state="validateState('tags')"
          >
            <v-select-custom
              id="article-tags"
              v-model="form.tags"
              :custom-props="{ multiple: true, taggable: true }"
              :options="tags"
              @search="fetchTags(form.lang, $event)"
              @option:created="addNewTag(form.lang, $event)"
            />
            <b-form-invalid
              id="input-tags-feedback"
              :label="$t('general.tags')"
              :validator="$v.form.tags"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.waterMark')"
            label-for="article-watermark"
          >
            <b-form-checkbox
              id="article-watermark"
              v-model="form.watermark"
              class="mt-1"
              name="article-waterMark"
              switch
              inline
            />
            <b-form-invalid
              id="input-watermark-feedback"
              :label="$t('general.waterMark')"
              :validator="$v.form.watermark"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="page-keywords"
            class="mb-2"
          >
            <b-form-input
              id="page-keywords"
              v-model="form.keywords"
              :state="validateState('keywords')"
            />
            <b-form-invalid
              id="input-keywords-feedback"
              :label="$t('general.keywords')"
              :validator="$v.form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          class="collapse-border"
        >
          <app-collapse>
            <app-collapse-item
              :is-visible="true"
              :title="$t('general.description')"
            >
              <b-form-group class="mb-2">
                <BFormTextarea
                  id="page-description"
                  v-model="form.description"
                  rows="5"
                  :state="validateState('description')"
                />
                <b-form-invalid
                  id="input-description-feedback"
                  :label="$t('general.description')"
                  :validator="$v.form.description"
                />
              </b-form-group>
            </app-collapse-item>
          </app-collapse>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group label-for="page-content">
            <CkeditorBasic
              id="page-content"
              v-model="form.content"
              :label="$t('general.content')"
              :state="validateState('content')"
            />
            <b-form-invalid
              id="input-content-feedback"
              :label="$t('general.content')"
              :validator="$v.form.content"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <div class="border rounded p-2">
            <h4 class="mb-1">
              {{ $t("general.thumbnail") }} - {{ $t("general.acceptImage") }}
            </h4>
            <b-file-input
              id="pageThumbnail"
              v-model="form.pageThumbnail"
              :state="validateState('pageThumbnail')"
              :alt.sync="form.pageThumbnailAlt"
              :alt-validator="$v.form.pageThumbnailAlt"
            >
              <template #invalidFeedback>
                <b-form-invalid
                  id="input-pageThumbnail-feedback"
                  :label="$t('general.thumbnail')"
                  :validator="$v.form.pageThumbnail"
                />
              </template>
            </b-file-input>
          </div>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <div class="border rounded p-2">
            <h4 class="mb-1">
              {{ $t("general.cover") }} - {{ $t("general.acceptImage") }}
            </h4>
            <b-file-input
              id="pageCoverImage"
              v-model="form.pageCoverImage"
              :state="validateState('pageCoverImage')"
              :alt.sync="form.pageCoverImageAlt"
              :alt-validator="$v.form.pageCoverImageAlt"
            >
              <template #invalidFeedback>
                <b-form-invalid
                  id="input-pageCoverImage-feedback"
                  :label="$t('general.cover')"
                  :validator="$v.form.pageCoverImage"
                />
              </template>
            </b-file-input>
          </div>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.submit") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>
<script>
import {
  BCard, BForm, BRow, BCol, BFormGroup, BFormInput, BButton, BFormTextarea, BFormCheckbox,
} from 'bootstrap-vue'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import BFileInput from '@/components/BFileInput/index.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import Ripple from 'vue-ripple-directive'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import languageSelector from '@/components/language-selector.vue'
import { isValidImageSize } from '@core/utils/rules'
import { required, minLength, requiredIf } from 'vuelidate/lib/validators'
import CkeditorBasic from '@/components/CkeditorBasic/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import usePagesList from './usePagesList'

export default {
  components: {
    AppCollapse,
    AppCollapseItem,
    BButton,
    BCard,
    BCol,
    BFileInput,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormTextarea,
    BRow,
    CkeditorBasic,
    LayoutLoading,
    VSelectCustom,
    languageSelector,
    BFormCheckbox,
  },
  directives: {
    Ripple,
  },
  validations() {
    return {
      form: {
        title: {
          required,
          minLength: minLength(3),
        },
        slug: {
          required,
          minLength: minLength(3),
          isUnique: () => this.isUniqueSlug,
        },
        tags: {
          required,
        },
        keywords: {
          required,
          minLength: minLength(3),
        },
        category: {
          required,
        },
        content: {
          required,
          minLength: minLength(10),
        },
        description: {
          required,
          minLength: minLength(10),
        },
        pageThumbnail: {
          required,
          isTrueImage: requiredIf(function () {
            return typeof this.form.pageThumbnail === 'object' && !Object.prototype.hasOwnProperty.call(this.form.pageThumbnail, '_id')
          }),
          isValidImageSize,
        },
        pageCoverImage: {
          required,
          isTrueImage: requiredIf(function () {
            return typeof this.form.pageCoverImage === 'object' && !Object.prototype.hasOwnProperty.call(this.form.pageCoverImage, '_id')
          }),
          isValidImageSize,
        },
        pageThumbnailAlt: {
          required,
          minLength: minLength(3),
        },
        pageCoverImageAlt: {
          required,
          minLength: minLength(3),
        },
      },
    }
  },
  setup() {
    const {
      addItem,
      addNewTag,
      checkSlugIsUnique,
      createTagIdArray,
      errorManager,
      fetchItem,
      fetchPageCategories,
      fetchTags,
      fetchUsersByRole,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      item,
      itemLoading,
      newTagIds,
      pageCategories,
      tags,
      updateItem,
    } = usePagesList()
    return {
      addItem,
      addNewTag,
      checkSlugIsUnique,
      createTagIdArray,
      fetchItem,
      fetchPageCategories,
      errorManager,
      fetchTags,
      form,
      fetchUsersByRole,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      item,
      itemLoading,
      newTagIds,
      pageCategories,
      tags,
      updateItem,
    }
  },
  data() {
    return {
      editorOptions: [],
    }
  },
  watch: {
    'form.title': function (newValue, oldValue) {
      this.$set(this.form, 'slug', this.createSlug(this.form.title))
      if (oldValue === this.form.pageThumbnailAlt) {
        this.$set(this.form, 'pageThumbnailAlt', newValue)
      }
      if (oldValue === this.form.pageCoverImageAlt) {
        this.$set(this.form, 'pageCoverImageAlt', newValue)
      }
    },
    'form.slug': function () {
      this.checkSlugIsUnique(this.form.lang, this.form.slug)
    },
  },
  created() {
    this.$set(this.form, 'lang', 'fa')
    this.fetchPageCategories('fa')
    this.fetchUsersByRole({
      role: 'editor',
    }).then(res => {
      this.editorOptions = res
    })
    this.fetchTags('fa')
  },
  methods: {
    updateAltImage() {
      if (!this.form.pageThumbnailAlt) {
        this.$set(this.form, 'pageThumbnailAlt', this.form.title)
      }
      if (!this.form.pageCoverImageAlt) {
        this.$set(this.form, 'pageCoverImageAlt', this.form.title)
      }
    },
    validateState(name) {
      const {
        $dirty,
        $error,
      } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    searchCategories(search) {
      this.fetchPageCategories(this.form.lang, search)
    },
    changeSelectedLanguage(selectedLanguage) {
      this.fetchPageCategories(selectedLanguage)
      this.fetchTags(selectedLanguage)
    },
    submitForm() {
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0].scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        }), 500)
        return
      }
      const uploadList = ['pageCoverImage', 'pageThumbnail']
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
    },
    sendData() {
      const data = {
        title: this.form.title,
        slug: this.form.slug,
        sort: this.form.sort,
        keywords: this.form.keywords,
        content: this.form.content,
        tagIds: this.createTagIdArray(this.form.tags, this.newTagIds),
        pageCategoryID: this.form.category._id,
        editorId: this.form.editor ? this.form.editor._id : null,
        description: this.form.description,
        pageCoverImageFileId: this.form.pageCoverImage ? this.form.pageCoverImage._id : null,
        pageThumbnailFileId: this.form.pageThumbnail ? this.form.pageThumbnail._id : null,
        watermark: this.form.watermark != null,
      }
      this.addItem(data, this.form.lang)
    },
  },
}
</script>
